import React from 'react';
import Lottie from 'lottie-react';
import { BaseModal, FilledButton, SvgIcon } from '@make-software/tg-mini-app-ui';

import { TxStatus } from '@/entities';
import TxSentIcon from '@/shared/assets/icons/ic-illustration-sent.svg';
import TxSentError from '@/shared/assets/icons/ic-illustration-wrong.svg';
import loadingAnimation from '@/shared/assets/lottie/loading-animation.json';
import CloseIcon from '@/shared/assets/icons/ic-close.svg';
import {
  CircleIconWrapper,
  ModalContentWrapper,
  processModalStyle,
  StyledCloseIcon,
  StyledTransactionProcessHeader
} from '@/shared';

import { AnimationContainer, Content, Title, Description } from '../styled';

interface IVoteProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: TxStatus;
}

const TITLES: Record<TxStatus, string> = {
  processing: 'Processing',
  processed: 'Vote sent!',
  error: 'Something went wrong'
};

const DESCRIPTIONS: Record<TxStatus, string | undefined> = {
  processing: 'Please wait a few moments while we confirm it on the network.',
  processed: undefined,
  error: "We couldn't process your vote. Please try again later."
};

export const VoteProjectModal: React.FC<IVoteProjectModalProps> = ({ isOpen, onClose, status }) => {
  const isProcessing = status === 'processing';
  const isSuccess = status === 'processed';

  const title = React.useMemo(() => TITLES[status], [status]);
  const description = React.useMemo(() => DESCRIPTIONS[status], [status]);
  const primaryLabel = isProcessing ? null : isSuccess ? 'Close' : null;

  const onPrimaryLabelClick = React.useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      variant="centered"
      centeredWidth="400px"
      centeredHeight="420px"
      style={processModalStyle}
    >
      <ModalContentWrapper direction="column" style={{ height: '100%', padding: '0 20px 20px 20px' }}>
        <StyledTransactionProcessHeader align="center" justify="flex-end">
          <CircleIconWrapper onClick={onClose} role="button" aria-label="Close modal">
            <StyledCloseIcon src={CloseIcon} />
          </CircleIconWrapper>
        </StyledTransactionProcessHeader>

        <Content align="center" isTransactionProcessing={isProcessing} style={{flexGrow: '1'}}>
          {isProcessing ? (
            <AnimationContainer align="center" justify="center">
              <Lottie animationData={loadingAnimation} style={{ height: 220, width: 220 }} loop />
            </AnimationContainer>
          ) : (
            <SvgIcon src={isSuccess ? TxSentIcon : TxSentError} size={180} />
          )}

          <Title weight={3} color="#FFFFFF">
            {title}
          </Title>
          {description && (
            <Description weight={3} isTransactionProcessing={isProcessing}>
              {description}
            </Description>
          )}

        </Content>
        {primaryLabel && <FilledButton title={primaryLabel} onClick={onPrimaryLabelClick}/>}
      </ModalContentWrapper>
    </BaseModal>
  );
};
